/* eslint-disable camelcase */
// CategorizedFoods.tsx
import React, { useState } from 'react';
import {
  Grid,
  Heading,
  HStack,
  Badge,
  useToast,
} from '@chakra-ui/react';
import { nhost } from 'src/lib/nhost';
import {
  useUpdateDishByPkMutation,
  Dish,
  GetRestaurantCategoryDishesQuery,
  GetAllCategoryDishesQuery,
  Order_By,
  useGetAllCategoriesQuery,
} from '../../utils/__generated__/graphql';
import { FoodCard } from './FoodCard';
import { DishModal } from './DishModal';
import { compressImage } from 'src/utils';
import ModernCard from './ModernCard';

interface CategorizedFoodsProps {
  dishesData: GetRestaurantCategoryDishesQuery | GetAllCategoryDishesQuery | undefined;
  refetch: () => void;
}

export const CategorizedFoods: React.FC<CategorizedFoodsProps> = ({ dishesData, refetch }) => {
  const toast = useToast();

  const { data: categoryData } = useGetAllCategoriesQuery({
    variables: {
      order_by: [{ name: Order_By.Asc }],
    },
  });

  const [updateDish, { loading: isUpdating }] = useUpdateDishByPkMutation({
    onCompleted: () => {
      refetch();
      toast({
        title: 'Dish Updated',
        status: 'success',
        duration: 2000,
      });
    },
  });

  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [selectedDish, setSelectedDish] = useState<any>({
    id: '',
    name: '',
    price: '',
    isActive: false,
    type: 'VEG',
    category: {
      id: '',
      name: '',
    },
  });

  const openEditModal = (dish: any) => {
    setSelectedDish({
      ...dish,
      categoryId: dish.category?.id || '', // Add this line to ensure categoryId is set
    });
    setEditModalOpen(true);
  };


  const handleUpdateDish = async (formData: any) => {
    try {
      await updateDish({
        variables: {
          pk_columns: { id: selectedDish.id },
          _set: {
            name: formData.name,
            price: formData.price,
            categoryId: formData.categoryId,
            type: formData.type,
            // image: formData.image,
          },
        },
      });
      setEditModalOpen(false);
    } catch (error: any) {
      toast({
        title: 'Error updating dish',
        description: error?.message,
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const previousFileId = selectedDish?.image;

    try {
      if (previousFileId) {
        await nhost.storage.delete({ fileId: previousFileId });
      }

      const compressedFile = await compressImage(file);
      const result = await nhost.storage.upload({
        file: compressedFile,
      });

      if (result?.fileMetadata?.id) {
        await updateDish({
          variables: {
            pk_columns: { id: selectedDish.id },
            _set: {
              image: result.fileMetadata.id,
            },
          },
        });

        // setSelectedDish({
        //   ...selectedDish,
        //   image: result.fileMetadata.id,
        // });

        // toast({
        //   title: 'Image Updated',
        //   status: 'success',
        //   duration: 2000,
        // });
      }
    } catch (error: any) {
      toast({
        title: 'Error updating image',
        description: error?.message,
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleToggleStatus = async (dish: Dish) => {
    try {
      await updateDish({
        variables: {
          pk_columns: { id: dish?.id },
          _set: {
            isActive: !dish?.isActive,
          },
        },
      });
    } catch (error: any) {
      toast({
        title: 'Error updating status',
        description: error?.message,
        status: 'error',
        duration: 3000,
      });
    }
  };

  return (
    <>
      {dishesData?.category.map((category) => {
        const dishCount = category?.dishes?.length ?? 0;
        if (dishCount === 0) return null;

        return (
          <ModernCard key={category.id} mb={8} p={4}>
            <HStack mb={4} justify="space-between">
              <Heading as="h4" size="lg" mb={1}>
                {category?.name}
              </Heading>
              <Badge colorScheme="brand" fontSize="lg">
                {dishCount} {dishCount === 1 ? 'Item' : 'Items'}
              </Badge>
            </HStack>

            <Grid
              templateColumns={{
                base: '1fr',
                md: 'repeat(2, 1fr)',
                lg: 'repeat(3, 1fr)',
                xl: 'repeat(4, 1fr)',
              }}
              gap={4}
            >
              {category?.dishes.map((dish) => (
                <FoodCard
                  key={dish?.id}
                  dish={dish as Dish}
                  handleToggleStatus={handleToggleStatus}
                  openEditModal={openEditModal}
                />
              ))}
            </Grid>
          </ModernCard>
        );
      })}

      <DishModal
        isOpen={isEditModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSubmit={handleUpdateDish}
        dish={selectedDish}
        categories={
          categoryData?.category.map((category) => ({
            value: category?.id ?? '',
            label: category?.name ?? '',
          })) ?? []
        }
        isLoading={isUpdating}
        handleFileChange={handleFileChange}
      />
    </>
  );
};
